import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/App';
import { auth, landing as landingApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Cpu, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Signup() {
  const [form, setForm] = useState({ email: '', password: '', full_name: '', company_name: '' });
  const [loading, setLoading] = useState(false);
  const [wcAmount, setWcAmount] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    landingApi.pricing().then(r => {
      const wc = r.data?.welcome_credit;
      if (wc?.enabled && wc?.amount) setWcAmount(wc.amount);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await auth.signup(form);
      login(res.data.token, res.data);
      navigate('/dashboard');
      toast.success('Account created!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    }
    setLoading(false);
  };

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div className="light-page min-h-screen flex items-center justify-center p-4" style={{background: `linear-gradient(to bottom right, #f8fafc, color-mix(in srgb, var(--brand-primary) 4%, white))`}}>
      <Card data-testid="signup-card" className="w-full max-w-md border-slate-200 bg-white shadow-xl shadow-slate-200/50">
        <CardHeader className="text-center space-y-3">
          <img src="/tensornex-logo.png" alt="Tensornex" className="mx-auto w-14 h-14 object-contain" />
          <CardTitle className="text-2xl text-slate-900" style={{fontFamily: "'Plus Jakarta Sans', sans-serif"}}>Create your account</CardTitle>
          <CardDescription>{wcAmount > 0 ? `Get $${wcAmount} in free credit to start building` : 'Start building with Tensornex'}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input data-testid="signup-name" placeholder="Jane Doe" value={form.full_name} onChange={e => update('full_name', e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label>Company <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input data-testid="signup-company" placeholder="Your company name" value={form.company_name} onChange={e => update('company_name', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input data-testid="signup-email" type="email" placeholder="you@company.com" value={form.email} onChange={e => update('email', e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input data-testid="signup-password" type="password" placeholder="Min 6 characters" value={form.password} onChange={e => update('password', e.target.value)} required minLength={6} />
            </div>
            <Button data-testid="signup-submit" type="submit" className="w-full brand-btn h-10 text-sm" disabled={loading}>
              {loading ? 'Creating...' : 'Create Account'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-4">
            Already have an account?{' '}
            <Link to="/login" className="brand-link font-medium">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
